import * as React from "react";
import {
  redirect,
  RouterProvider,
  createBrowserRouter,
  Navigate,
  useParams
} from "react-router-dom";
import { PrivateRoute } from "./PrivateRoute";
import { GatedAdminFeature, GatedSubscriptionFeature } from "@/pages/Messaging/Integrations/shared/GatedIntegration";
import { BackToIntegrations } from "@/pages/Messaging/Integrations/shared/IntegrationPageWrapper";
import AuthService from "@/services/AuthService";
import { RouteHeader, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import AgreementGate from "@/components/AgreementGate";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";

// Header action components — must be eager so they render before the lazy page resolves
import CreateChatbotButton from "@/pages/Messaging/Bots/AI/CreateChatbotButton";
import NewWorkflowButton from "@/pages/Messaging/Workflows/NewWorkflowButton";
import SaveChatbotButton from "@/pages/Messaging/Bots/AI/SaveChatbotButton";
import CreateChatWindowButton from "@/pages/Messaging/ChatWindow/CreateChatWindowButton";
import AINewCreateChatbotButton from "@/pages/Messaging/Bots/AINew/AINewCreateChatbotButton";
import AINewSaveChatbotButton from "@/pages/Messaging/Bots/AINew/AINewSaveChatbotButton";
import CreateIndexButton from "@/pages/Messaging/Bots/AI/CreateIndexButton";
import AddPhoneNumberButton from "@/pages/Messaging/Channels/AddPhoneNumberButton";
import SearchBar from "@/components/SearchBar";

// Integration pages with named BackTo exports — static import required because the named
// export is used directly in headerActions (a static import forces the module to load eagerly;
// splitting these would require moving BackTo components to separate files).
import NetSuite, {
  BackToIntegrationLanding
} from "@/pages/Messaging/Integrations/NetSuite/NetSuite";
import UserProfile, {
  BackToUsersButton
} from "@/pages/Messaging/Users/UserProfile";
import ShopifyCreate, {
  ShopifyBackToShopifyOverview
} from "@/pages/Messaging/Integrations/Shopify/ShopifyCreate";
import ShopifyOverview, {
  ShopifyBackToIntegrationLanding
} from "@/pages/Messaging/Integrations/Shopify/ShopifyOverview";
import HubSpot, {
  BackToIntegrationLandingHubSpot
} from "@/pages/Messaging/Integrations/HubSpot/HubSpot";
import HubSpotOverview, {
  HubSpotBackToIntegrationLanding,
  HubSpotBackToHubSpotOverview
} from "@/pages/Messaging/Integrations/HubSpot/HubSpotOverview";
import Salesforce, {
  BackToIntegrationLandingSalesforce
} from "@/pages/Messaging/Integrations/Salesforce/Salesforce";
import ServiceNow, {
  BackToIntegrationLandingServiceNow
} from "@/pages/Messaging/Integrations/ServiceNow/ServiceNow";
import Uplisting, {
  BackToIntegrationLandingUplisting
} from "@/pages/Messaging/Integrations/Uplisting/Uplisting";
import Guesty, {
  BackToIntegrationLandingGuesty
} from "@/pages/Messaging/Integrations/Guesty/Guesty";
import Cloudbeds, {
  BackToIntegrationLandingCloudbeds
} from "@/pages/Messaging/Integrations/Cloudbeds/Cloudbeds";
import Pipedrive, {
  BackToIntegrationLandingPipedrive
} from "@/pages/Messaging/Integrations/Pipedrive/Pipedrive";
import QuickBooks, {
  BackToIntegrationLandingQuickBooks
} from "@/pages/Messaging/Integrations/QuickBooks/QuickBooks";
import Zoho, {
  BackToIntegrationLandingZoho
} from "@/pages/Messaging/Integrations/Zoho/Zoho";
import Toast, {
  BackToIntegrationLandingToast
} from "@/pages/Messaging/Integrations/Toast/Toast";
import Clover, {
  BackToIntegrationLandingClover
} from "@/pages/Messaging/Integrations/Clover/index";
import Mindbody, {
  BackToIntegrationLandingMindbody
} from "@/pages/Messaging/Integrations/Mindbody/Mindbody";
import Monday, {
  BackToIntegrationLandingMonday
} from "@/pages/Messaging/Integrations/Monday/Monday";
import Recruiting, {
  BackToIntegrationLandingRecruiting
} from "@/pages/Messaging/Integrations/Recruiting/Recruiting";
import Jobber, {
  BackToIntegrationLandingJobber
} from "@/pages/Messaging/Integrations/Jobber/index";
import MicrosoftBookings, {
  BackToIntegrationLandingMicrosoftBookings
} from "@/pages/Messaging/Integrations/MicrosoftBookings/MicrosoftBookings";
import OpenTableIntegration, {
  BackToIntegrationLandingOpenTable
} from "@/pages/Messaging/Integrations/OpenTable/index";
import ResyIntegration, {
  BackToIntegrationLandingResy
} from "@/pages/Messaging/Integrations/Resy/index";
import BigCommerce, {
  BackToIntegrationLandingBigCommerce
} from "@/pages/Messaging/Integrations/BigCommerce/BigCommerce";
import WooCommerce, {
  BackToIntegrationLandingWooCommerce
} from "@/pages/Messaging/Integrations/WooCommerce/WooCommerce";
import BigCommerceOverview, {
  BigCommerceBackToIntegrationLanding,
  BigCommerceBackToOverview
} from "@/pages/Messaging/Integrations/BigCommerce/BigCommerceOverview";
import Magento, {
  BackToIntegrationLandingMagento
} from "@/pages/Messaging/Integrations/Magento/Magento";
import MagentoOverview, {
  MagentoBackToIntegrationLanding,
  MagentoBackToOverview
} from "@/pages/Messaging/Integrations/Magento/MagentoOverview";
import Dynamics, {
  BackToIntegrationLandingDynamics
} from "@/pages/Messaging/Integrations/Dynamics/Dynamics";
import DynamicsOverview, {
  DynamicsBackToIntegrationLanding,
  DynamicsBackToOverview
} from "@/pages/Messaging/Integrations/Dynamics/DynamicsOverview";
import NativeCrm, {
  BackToIntegrationLandingNativeCrm
} from "@/pages/Messaging/Integrations/NativeCrm/NativeCrm";
import KlaviyoIntegration, {
  BackToIntegrationLandingKlaviyo
} from "@/pages/Messaging/Integrations/Klaviyo/Klaviyo";
import Vast, {
  BackToIntegrationLandingVast
} from "@/pages/Messaging/Integrations/Vast/Vast";
import NextivaPage, {
  BackToIntegrationLandingNextiva
} from "@/pages/Messaging/Integrations/Nextiva/Nextiva";
import TeamsPhonePage, {
  BackToIntegrationLandingTeamsPhone
} from "@/pages/Messaging/Integrations/TeamsPhone/TeamsPhone";
import SkySwitchPage, {
  BackToIntegrationLandingSkySwitch
} from "@/pages/Messaging/Integrations/SkySwitch/SkySwitch";
import SecureFormsPanel, {
  BackToIntegrationLandingSecureForms
} from "@/pages/Messaging/Integrations/SecureForms/SecureFormsPanel";
import GorgiasWizard, {
  BackToIntegrationLandingGorgias
} from "@/pages/Messaging/Integrations/Gorgias/GorgiasWizard";
import GoogleTagManager, {
  BackToIntegrationLandingGTM
} from "@/pages/Messaging/Integrations/GoogleTagManager/GoogleTagManager";
const ServiceTitanPage = React.lazy(() => import("@/pages/Messaging/Integrations/ServiceTitan/ServiceTitan"));
const AirtablePage = React.lazy(() => import("@/pages/Messaging/Integrations/Airtable/Airtable"));

// Lazy-loaded page components — Vite code-splits these into separate chunks.
// Each chunk only downloads when the user navigates to that route.
const ContactCenter = React.lazy(() => import("@/pages/ContactCenter"));
const Home = React.lazy(() => import("@/pages/Home"));
const EntraCallback = React.lazy(() => import("@/pages/Auth/EntraCallback"));
const LoginCallback = React.lazy(() => import("@/pages/Auth/LoginCallback"));
const LoginCallbackError = React.lazy(() => import("@/pages/Auth/LoginCallbackError"));
const LoginUnauthorized = React.lazy(() => import("@/pages/Auth/LoginUnauthorized"));
const Logout = React.lazy(() => import("@/pages/Auth/Logout"));
const LogoutCallback = React.lazy(() => import("@/pages/Auth/LogoutCallback"));
const AutoResponses = React.lazy(() => import("@/pages/Messaging/AutoResponses/AutoResponses"));
const CannedResponses = React.lazy(() => import("@/pages/Messaging/CannedResponses/CannedResponses"));
const TwitterCallback = React.lazy(() => import("@/pages/Messaging/Channels/TwitterCallback"));
const IntegrationLog = React.lazy(() => import("@/pages/Messaging/Integrations/Log"));
const NetSuiteCreate = React.lazy(() => import("@/pages/Messaging/Integrations/NetSuite/NetSuiteCreate"));
const NetSuiteCallback = React.lazy(() => import("@/pages/Messaging/Integrations/NetSuite/NetSuiteCallback"));
const Surveys = React.lazy(() => import("@/pages/Messaging/Surveys/Surveys"));
const Teams = React.lazy(() => import("@/pages/Messaging/Teams/Teams"));
const WebMessenger = React.lazy(() => import("@/pages/Messaging/WebMessenger"));
const WorkSchedules = React.lazy(() => import("@/pages/Messaging/WorkSchedules/WorkSchedules"));
const Users = React.lazy(() => import("@/pages/Messaging/Users/Users"));
const AddUserWizard = React.lazy(() => import("@/pages/Messaging/Users/AddUserWizard/AddUserWizard"));
const Deployment = React.lazy(() => import("@/pages/Messaging/Deployments/Deployments"));
const Schedules = React.lazy(() => import("@/pages/Messaging/Schedules/Schedules"));
const BotManager = React.lazy(() => import("@/pages/Messaging/Bots"));
const RuleManager = React.lazy(() => import("@/pages/Messaging/Rules"));
const RoutingHistory = React.lazy(() => import("@/pages/Messaging/Rules/RoutingHistory"));
const WhatsRunning = React.lazy(() => import("@/pages/Messaging/WhatsRunning/WhatsRunning"));
const ChatPreview = React.lazy(() => import("@/pages/Messaging/ChatPreview/ChatPreview"));
const Landing = React.lazy(() => import("@/pages/Messaging/Integrations/IntegrationsLanding"));
const FileStorage = React.lazy(() => import("@/pages/Messaging/FileStorage"));
// WorkflowsEditor and WorkflowsCreate include @xyflow/react (canvas lib) — deferring saves ~500KB on initial load
const WorkflowsCreate = React.lazy(() => import("@/pages/Messaging/Workflows/WorkflowsCreate"));
const Workflows = React.lazy(() => import("@/pages/Messaging/Workflows/Workflows"));
const WorkflowsLandingGate = React.lazy(() => import("@/pages/Messaging/Workflows/WorkflowsLandingGate"));
const WorkflowsEditor = React.lazy(() => import("@/pages/Messaging/Workflows/WorkflowsEditor"));
const VariableGuide = React.lazy(() => import("@/pages/Messaging/Workflows/VariableGuide"));
const SettingsHub = React.lazy(() => import("@/pages/SettingsHub/SettingsHub"));
const ConditionPresetsPage = React.lazy(() => import("@/pages/Settings/ConditionPresets/ConditionPresetsPage"));
const FacebookIntegration = React.lazy(() => import("@/pages/Messaging/Channels/FacebookIntegration"));
const InstagramIntegration = React.lazy(() => import("@/pages/Messaging/Channels/InstagramIntegration"));
const TikTokIntegration = React.lazy(() => import("@/pages/Messaging/Channels/TikTokIntegration"));
const TikTokCallback = React.lazy(() => import("@/pages/Messaging/Channels/TikTokCallback"));
const SmsIntegration = React.lazy(() => import("@/pages/Messaging/Channels/SmsIntegration"));
const WhatsAppIntegration = React.lazy(() => import("@/pages/Messaging/Channels/WhatsAppIntegration"));
const RcsIntegration = React.lazy(() => import("@/pages/Messaging/Channels/RcsIntegration"));
const AppleMessagesIntegration = React.lazy(() => import("@/pages/Messaging/Channels/AppleMessagesIntegration"));
const IvrIntegration = React.lazy(() => import("@/pages/Messaging/Channels/IvrIntegration"));
// ArticlesCreate and ArticlesEdit include CKEditor5 + TipTap — deferring saves ~2MB on initial load
const ArticlesIndex = React.lazy(() => import("@/kb/articles/Index"));
const ArticlesCreate = React.lazy(() => import("@/kb/articles/Create"));
const ArticlesEdit = React.lazy(() => import("@/kb/articles/Edit"));
const SettingsIndex = React.lazy(() => import("@/kb/settings/Index"));
const TopicsIndex = React.lazy(() => import("@/kb/topics/Index"));
const SiteMap = React.lazy(() => import("@/kb/sitemap/Index"));
const SetupIndex = React.lazy(() => import("@/kb/setup/Index"));
const KBPerformance = React.lazy(() => import("@/kb/performance/Index"));
const KBWidgetEmbedCode = React.lazy(() => import("@/kb/widget/EmbedCode"));
const AssetsIndex = React.lazy(() => import("@/kb/assets/Index"));
const Skills = React.lazy(() => import("@/pages/Messaging/Skills/Skills"));
const MessagingSetup = React.lazy(() => import("@/pages/Messaging/Setup/Index"));
// Admin pages — rarely accessed, no reason to pay their cost on initial load
const ActivityLog = React.lazy(() => import("@/pages/Admin/ActivityLog"));
const ToolUsageReport = React.lazy(() => import("@/pages/Admin/ToolUsageReport"));
const VelaroLogger = React.lazy(() => import("@/pages/Admin/VelaroLogger"));
const AgentLoginLogs = React.lazy(() => import("@/pages/Admin/AgentLoginLogs"));
const UserRoles = React.lazy(() => import("@/pages/Messaging/UserRoles"));
const CopilotAdmin = React.lazy(() => import("@/pages/Admin/CopilotAdmin"));
const PricingQuote = React.lazy(() => import("@/pages/Admin/PricingQuote"));
const DatabaseTool = React.lazy(() => import("@/pages/Admin/DatabaseTool"));
const DeployHistory = React.lazy(() => import("@/pages/Superadmin/DeployHistory"));
const SearchIndexes = React.lazy(() => import("@/pages/Admin/SearchIndexes"));
const CampaignHealth = React.lazy(() => import("@/pages/Admin/CampaignHealth"));
const DetectionReport = React.lazy(() => import("@/pages/Admin/DetectionReport"));
const PlatformChannelSetup = React.lazy(() => import("@/pages/Admin/PlatformChannelSetup"));
const MFAthleticDemo = React.lazy(() => import("@/pages/Admin/MFAthleticDemo"));
const LiveMonitor = React.lazy(() => import("@/pages/Admin/LiveMonitor"));
const AccountHealth = React.lazy(() => import("@/pages/Admin/AccountHealth"));
const AccountManagerAssignments = React.lazy(() => import("@/pages/Admin/AccountManagerAssignments"));
const AIPromptPolicy = React.lazy(() => import("@/pages/Admin/AIPromptPolicy"));
const AppMarketplace = React.lazy(() => import("@/pages/Admin/AppMarketplace/AppMarketplace"));
const SiteSync = React.lazy(() => import("@/pages/Admin/SiteSync"));
const Deployments = React.lazy(() => import("@/pages/Admin/Deployments"));
const SubscriptionSettings = React.lazy(() => import("@/pages/Admin/SubscriptionSettings"));
const BillingFinancial = React.lazy(() => import("@/pages/Admin/BillingFinancial"));
const BillingStaff = React.lazy(() => import("@/pages/Admin/BillingStaff"));
const BillingPlans = React.lazy(() => import("@/pages/Admin/BillingPlans"));
const BillingOverage = React.lazy(() => import("@/pages/Admin/BillingOverage"));
const KBDomains = React.lazy(() => import("@/pages/Admin/KBDomains"));
const HangfireMonitor = React.lazy(() => import("@/pages/Admin/HangfireMonitor"));
const SupportTools = React.lazy(() => import("@/pages/Admin/SupportTools"));
// Bot / AI pages
const AI = React.lazy(() => import("@/pages/Messaging/Bots/AI/AI"));
const AIChatbotSettingsGeneral = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsGeneral"));
const AIChatbotSettingsTraining = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsTraining"));
const AIChatbotSettingsHandoff = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsHandoff"));
const AIChatbotSettingsPlayground = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsPlayground"));
const AIChatbotSettingsTest = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsTest"));
const AIChatbotSettingsKnowledgeOverrides = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsKnowledgeOverrides"));
const AIChatbotSettingsSuppressedContent = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsSuppressedContent"));
const AIChatbotSettingsSkills = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotSettingsSkills"));
const AIChatbotTrainingIndex = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotTrainingIndex"));
const AIChatbotSettingsTrainingTabFile = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotFileTrainingIndex"));
const AIChatbotFileTraining = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotFileTraining"));
const AIChatbotWebTraining = React.lazy(() => import("@/pages/Messaging/Bots/AI/AIChatbotWebTraining"));
const BuildBotWizard = React.lazy(() => import("@/pages/Messaging/Bots/BuildBotWizard"));
const LanguageSetupWizard = React.lazy(() => import("@/pages/Messaging/LanguageSetupWizard/LanguageSetupWizard"));
const AINewTraining = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewTraining"));
const AINewChat = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewChat"));
const AINewSettings = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettings"));
const AINewSettingsGeneral = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsGeneral"));
const AINewSettingsHandoff = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsHandoff"));
const AINewSettingsPlayground = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsPlayground"));
const AINewIndexes = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewIndexes"));
const AINewSettingsKnowledgeOverrides = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsKnowledgeOverrides"));
const AINewSettingsSkills = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsSkills"));
const AINewSettingsChannelOverrides = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsChannelOverrides"));
const AINewSettingsQnA = React.lazy(() => import("@/pages/Messaging/Bots/AINew/AINewSettingsQnA"));
// Integration pages without named headerAction exports
const AccelaCivic = React.lazy(() => import("@/pages/Messaging/Integrations/Accela/Accela"));
const AccelaCivicCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Accela/AccelaCreate"));
const AccelaCallbackBridge = React.lazy(() => import("@/pages/Messaging/Integrations/Accela/AccelaCallbackBridge"));
const Formstack = React.lazy(() => import("@/pages/Messaging/Integrations/Formstack/Formstack"));
const FormstackCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Formstack/FormstackCreate"));
const ServiceNowCreate = React.lazy(() => import("@/pages/Messaging/Integrations/ServiceNow/ServiceNowCreate"));
const SAP = React.lazy(() => import("@/pages/Messaging/Integrations/SAP/SAP"));
const SapCreate = React.lazy(() => import("@/pages/Messaging/Integrations/SAP/SapCreate"));
const UplistingCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Uplisting/UplistingCreate"));
const GuestyCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Guesty/GuestyCreate"));
const CloudbedsCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Cloudbeds/CloudbedsCreate"));
const MindbodyCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Mindbody/MindbodyCreate"));
const HubSpotCreate = React.lazy(() => import("@/pages/Messaging/Integrations/HubSpot/HubSpotCreate"));
const BigCommerceCreate = React.lazy(() => import("@/pages/Messaging/Integrations/BigCommerce/BigCommerceCreate"));
const MagentoCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Magento/MagentoCreate"));
const DynamicsCreate = React.lazy(() => import("@/pages/Messaging/Integrations/Dynamics/DynamicsCreate"));
const Shopify = React.lazy(() => import("@/pages/Messaging/Integrations/Shopify/Shopify"));
const McpServers = React.lazy(() => import("@/pages/Messaging/Integrations/MCP/McpServers"));
const StripePayments = React.lazy(() => import("@/pages/Messaging/Integrations/Stripe"));
const PowerAutomate = React.lazy(() => import("@/pages/Messaging/Integrations/PowerAutomate"));
const MakeIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Make"));
const ZapierIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Zapier"));
const TranscriptStorage = React.lazy(() => import("@/pages/Messaging/Integrations/TranscriptStorage"));
const Microsoft365 = React.lazy(() => import("@/pages/Messaging/Integrations/Microsoft365"));
const TeamsNotifications = React.lazy(() => import("@/pages/Messaging/Integrations/Microsoft365/TeamsNotifications"));
const OneDriveSettingsPage = React.lazy(() => import("@/pages/Messaging/Integrations/Microsoft365/OneDriveSettings"));
const SlackNotificationsPage = React.lazy(() => import("@/pages/Messaging/Integrations/SlackNotifications"));
const AlertRulesPage = React.lazy(() => import("@/pages/Messaging/Integrations/AlertRules"));
const GmailInbox = React.lazy(() => import("@/pages/Messaging/Integrations/GmailInbox"));
const OutlookInbox = React.lazy(() => import("@/pages/Messaging/Integrations/OutlookInbox"));
const OutlookCalendar = React.lazy(() => import("@/pages/Messaging/Integrations/OutlookCalendar"));
const CalendlyIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Calendly"));
const SquareIntegration = React.lazy(() => import("@/pages/Messaging/Integrations/Square"));
const SquareCallbackPage = React.lazy(() => import("@/pages/Messaging/Integrations/Square/Callback"));
const Webflow = React.lazy(() => import("@/pages/Messaging/Integrations/Webflow/Webflow"));
const WebflowConnect = React.lazy(() => import("@/pages/Messaging/Integrations/Webflow/WebflowConnect"));
// Chat UI pages
const ChatWindow = React.lazy(() => import("@/pages/Messaging/ChatWindow"));
const ChatWindowEditor = React.lazy(() => import("@/pages/Messaging/ChatWindow/ChatWindowEditor"));
// Other pages
const CustomFields = React.lazy(() => import("@/pages/ContactCenter/CustomFields"));
const ContactSegments = React.lazy(() => import("@/pages/ContactCenter/ContactSegments"));
const TeamsChannel = React.lazy(() => import("@/pages/Messaging/Integrations/Teams/TeamsChannel"));
const MessagingSettings = React.lazy(() => import("@/pages/Messaging/Settings"));
const BillingPage = React.lazy(() => import("@/pages/Messaging/Billing"));
const SiteSettings = React.lazy(() => import("@/pages/Messaging/SiteSettings"));
const TranscriptSettings = React.lazy(() => import("@/pages/Messaging/SiteSettings/TranscriptSettings"));
const AIProviderSettings = React.lazy(() => import("@/pages/Messaging/SiteSettings/AIProviderSettings"));
const Tags = React.lazy(() => import("@/pages/Messaging/Tags/Tags"));
const Invitations = React.lazy(() => import("@/pages/Messaging/Invitations"));
const BaseInvitationEditor = React.lazy(() => import("@/pages/Messaging/Invitations/BaseInvitationEditor"));
const PageWidgets = React.lazy(() => import("@/pages/Messaging/PageWidgets"));
const ProactiveEngagement = React.lazy(() => import("@/pages/Messaging/ProactiveEngagement"));
const VisitorMonitor = React.lazy(() => import("@/pages/Messaging/VisitorMonitor"));
// EmailCampaigns includes grapesjs (email editor) — deferring saves ~800KB on initial load
const EmailCampaigns = React.lazy(() => import("@/pages/Messaging/EmailCampaigns"));
const EmailFlows = React.lazy(() => import("@/pages/Messaging/EmailFlows"));
const IvrCampaigns = React.lazy(() => import("@/pages/Messaging/IvrCampaigns"));
const Packages = React.lazy(() => import("@/pages/Messaging/Packages"));
const ContactSupport = React.lazy(() => import("@/pages/Support/ContactSupport"));
// Reports pages include recharts — deferring keeps charting code out of the initial bundle
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const ValueReport = React.lazy(() => import("@/pages/Reports/ValueReport"));
const AgentPerformance = React.lazy(() => import("@/pages/Reports/AgentPerformance"));
const Leaderboard = React.lazy(() => import("@/pages/Reports/Leaderboard"));
const ChatIntelligence = React.lazy(() => import("@/pages/Reports/ChatIntelligence"));
const CsatReport = React.lazy(() => import("@/pages/Reports/CsatReport"));
const ConversionReport = React.lazy(() => import("@/pages/Reports/ConversionReport"));
const AppleMessagesReport = React.lazy(() => import("@/pages/Reports/AppleMessagesReport"));
const Conversions = React.lazy(() => import("@/pages/Messaging/Settings/Conversions"));
const ClickToCallPage = React.lazy(() => import("@/pages/Messaging/ClickToCall"));
const AvatarSettings = React.lazy(() => import("@/pages/Messaging/Avatar/AvatarSettings"));
// Billing pages
const Contracts = React.lazy(() => import("@/pages/Billing/Contracts"));
const ContractTemplates = React.lazy(() => import("@/pages/Billing/ContractTemplates"));
const SigningPage = React.lazy(() => import("@/pages/Billing/SigningPage"));
const BillingInvoices = React.lazy(() => import("@/pages/Billing/Invoices"));
const BillingTemplates = React.lazy(() => import("@/pages/Billing/InvoiceTemplates"));
const BillingStripeSettings = React.lazy(() => import("@/pages/Billing/StripeSettings"));
const QuoteAcceptPage = React.lazy(() => import("@/pages/Billing/QuoteAcceptPage"));
const QuotePublicPage = React.lazy(() => import("@/pages/Billing/QuotePublicPage"));
const NotFoundPage = React.lazy(() => import("@/pages/Shared/NotFoundPage"));
const MarketplaceActivation = React.lazy(() => import("@/pages/Marketplace/MarketplaceActivation"));

const handleSearch = (searchText: string) => {};

// Redirect helper — reads :id from the route and sends to the equivalent /ai-new/ tab
function RedirectAIToNew({ suffix }: { suffix: string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/ai-new/settings/${suffix}/${id ?? ""}`} replace />;
}

const handleAccountSearch = (searchText: string) => {
  window.dispatchEvent(
    new CustomEvent("accountSearch", { detail: searchText })
  );
};

const createAppRoutes = (mainLoader: (params: any) => any) => [
  { path: "auth/callback", element: <EntraCallback /> },
  { path: "login/callback", element: <LoginCallback /> },
  { path: "login/callback/error", element: <LoginCallbackError /> },
  { path: "sign/:token", element: <SigningPage /> },
  { path: "quote/:token", element: <QuoteAcceptPage /> },
  { path: "q/:token", element: <QuotePublicPage /> },
  { path: "login/unauthorized", element: <LoginUnauthorized /> },
  { path: "logout", element: <Logout /> },
  { path: "logout/callback", element: <LogoutCallback /> },
  // AppSource marketplace activation — must be public (buyers arrive before they have a Velaro account)
  { path: "marketplace/activate", element: <MarketplaceActivation /> },
  {
    element: <PrivateRoute />,
    children: [
      { path: "/", element: <Home /> },
      {
        path: "/dashboard",
        element: (
          <RouteHeader title="Live Operations Dashboard">
            <Dashboard />
          </RouteHeader>
        )
      },
      {
        path: "/account",
        element: <SiteSettings />
      },
      {
        path: "/account/transcript-settings",
        element: (
          <RouteHeader title="Transcript Settings">
            <TranscriptSettings />
          </RouteHeader>
        )
      },
      {
        path: "/admin/deploy-history",
        element: <DeployHistory />
      },
      {
        path: "/admin/database-tool",
        element: (
          <RouteHeader title="Database Administration">
            <DatabaseTool />
          </RouteHeader>
        )
      },
      {
        path: "/admin/hangfire",
        element: (
          <RouteHeader title="Hangfire Monitor">
            <HangfireMonitor />
          </RouteHeader>
        )
      },
      {
        path: "/admin/search-indexes",
        element: (
          <RouteHeader title="Search Index Management">
            <SearchIndexes />
          </RouteHeader>
        )
      },
      {
        path: "/admin/site-sync",
        element: (
          <RouteHeader title="Site Sync">
            <SiteSync />
          </RouteHeader>
        )
      },
      {
        path: "/admin/deployments",
        element: (
          <RouteHeader title="Deployments">
            <Deployments />
          </RouteHeader>
        )
      },
      {
        path: "/admin/subscription-settings",
        element: (
          <RouteHeader title="Subscription Settings">
            <SubscriptionSettings />
          </RouteHeader>
        )
      },
      {
        path: "/admin/kb-domains",
        element: (
          <RouteHeader title="KB Custom Domains">
            <KBDomains />
          </RouteHeader>
        )
      },
      {
        path: "/admin/app-marketplace",
        element: (
          <RouteHeader title="App Marketplace Credentials">
            <AppMarketplace />
          </RouteHeader>
        )
      },
      {
        path: "/admin/campaign-health",
        element: (
          <RouteHeader title="Campaign Health">
            <CampaignHealth />
          </RouteHeader>
        )
      },
      {
        path: "/admin/detection-report",
        element: (
          <RouteHeader title="Site Detection Report">
            <DetectionReport />
          </RouteHeader>
        )
      },
      {
        path: "/admin/copilot",
        element: (
          <RouteHeader title="AI Platform">
            <CopilotAdmin />
          </RouteHeader>
        )
      },
      {
        path: "/admin/pricing-quote",
        element: (
          <RouteHeader title="Pricing & Quote Builder">
            <PricingQuote />
          </RouteHeader>
        )
      },
      {
        path: "/admin/platform-setup",
        element: (
          <RouteHeader title="Platform Channel Setup">
            <PlatformChannelSetup />
          </RouteHeader>
        )
      },
      {
        path: "/admin/mf-athletic-demo",
        element: (
          <RouteHeader title="MF Athletic Demo">
            <MFAthleticDemo />
          </RouteHeader>
        )
      },
      {
        path: "/admin/ai-prompt-policy",
        element: (
          <RouteHeader title="AI Prompt Policies">
            <AIPromptPolicy />
          </RouteHeader>
        )
      },
      {
        path: "/admin/live-monitor",
        element: <LiveMonitor />
      },
      {
        path: "/admin/account-health",
        element: <AccountHealth />
      },
      {
        path: "/admin/am-assignments",
        element: <AccountManagerAssignments />
      },
      {
        path: "/admin/billing-overage",
        element: (
          <RouteHeader title="Billing Overage Settings">
            <BillingOverage />
          </RouteHeader>
        )
      },
      {
        path: "/admin/billing-financial",
        element: (
          <RouteHeader title="Financial Dashboard">
            <BillingFinancial />
          </RouteHeader>
        )
      },
      {
        path: "/admin/billing-staff",
        element: (
          <RouteHeader title="Billing Staff">
            <BillingStaff />
          </RouteHeader>
        )
      },
      {
        path: "/admin/billing/plans",
        element: (
          <RouteHeader title="Plan Management">
            <BillingPlans />
          </RouteHeader>
        )
      },
      {
        path: "/admin/activity-log",
        element: (
          <RouteHeader title="Activity Log">
            <ActivityLog />
          </RouteHeader>
        )
      },
      {
        path: "/admin/velaro-logger",
        element: (
          <RouteHeader title="Velaro Logger">
            <VelaroLogger />
          </RouteHeader>
        )
      },
      {
        path: "/admin/agent-login-logs",
        element: (
          <RouteHeader title="Agent Login History">
            <AgentLoginLogs />
          </RouteHeader>
        )
      },
      {
        path: "/admin/support-tools",
        element: (
          <RouteHeader title="Support Tools">
            <SupportTools />
          </RouteHeader>
        )
      },
      {
        path: "/admin/tool-usage",
        element: (
          <RouteHeader title="Tool Usage Report">
            <ToolUsageReport />
          </RouteHeader>
        )
      },
      {
        path: "/settings/roles",
        element: (
          <RouteHeader title="Roles & Permissions">
            <UserRoles />
          </RouteHeader>
        )
      },
      {
        path: "/settings/ai-provider",
        element: (
          <RouteHeader title="AI Provider">
            <AIProviderSettings />
          </RouteHeader>
        )
      },
      {
        path: "/settings/hub",
        element: (
          <RouteHeader title="Settings">
            <SettingsHub />
          </RouteHeader>
        )
      },
      {
        path: "/settings/condition-presets",
        element: (
          <RouteHeader title="Condition Presets">
            <ConditionPresetsPage />
          </RouteHeader>
        )
      },
      {
        path: "/users/",
        element: <Users />
      },
      {
        path: "/users/:userId",
        element: (
          <RouteHeader
            title="User Profile"
            headerActions={<BackToUsersButton />}
          >
            <UserProfile />
          </RouteHeader>
        )
      },
      {
        path: "/user/add",
        element: (
          <RouteHeader title="Add User">
            <AddUserWizard />
          </RouteHeader>
        )
      },
      {
        path: "/contact_center/*",
        element: (
          <RouteHeader title="Contact Center">
            <ContactCenter />
          </RouteHeader>
        )
      },
      {
        path: "/contact-segments",
        element: (
          <RouteHeader title="Contact Segments">
            <ContactSegments />
          </RouteHeader>
        )
      },
      {
        path: "/teams",
        element: <Teams />
      },
      {
        path: "/schedules",
        element: <Schedules />
      },
      {
        path: "/deployment",
        element: (
          <StickyRouteHeader
            title="Deployment"
          >
            <AgreementGate slug="deployment-compliance">
              <Deployment />
            </AgreementGate>
          </StickyRouteHeader>
        )
      },
      {
        path: "/tags",
        element: (
          <RouteHeader title="Tags">
            <Tags />
          </RouteHeader>
        )
      },
      {
        path: "/custom-fields",
        element: (
          <RouteHeader title="Custom Fields">
            <CustomFields />
          </RouteHeader>
        )
      },
      {
        path: "/facebook",
        element: (
          <RouteHeader title="Facebook">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <FacebookIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/instagram",
        element: (
          <RouteHeader title="Instagram">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <InstagramIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/tiktok",
        element: (
          <RouteHeader title="TikTok">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <TikTokIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/tiktok/callback",
        element: <TikTokCallback />
      },
      {
        path: "/sms",
        element: (
          <RouteHeader title="SMS">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <SmsIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/whatsapp",
        element: (
          <RouteHeader title="WhatsApp">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <WhatsAppIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/rcs",
        element: (
          <RouteHeader title="RCS Business Messaging">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <RcsIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/apple-messages",
        element: (
          <RouteHeader title="Apple Messages for Business">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <AppleMessagesIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/ivr",
        element: (
          <RouteHeader title="IVR" headerActions={<AddPhoneNumberButton />}>
            <IvrIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/click-to-call",
        element: (
          <RouteHeader title="Click-to-Call">
            <ClickToCallPage />
          </RouteHeader>
        )
      },
      {
        path: "/avatar",
        element: (
          <RouteHeader title="AI Video Avatars">
            <AvatarSettings />
          </RouteHeader>
        )
      },
      {
        path: "/surveys",
        element: (
          <RouteHeader title="Surveys">
            <Surveys />
          </RouteHeader>
        )
      },
      {
        path: "/integrations",
        element: (
          <StickyRouteHeader
            title="Integrations"
            subTitle="Configure integrations to fit your specific business needs, enhancing your ability to manage everything in one place."
            headerActions={
              <SearchBar onSearch={handleSearch} placeholderText="Search" />
            }
          >
            <Landing />
          </StickyRouteHeader>
        )
      },
      {
        path: "/twitter/callback",
        element: <TwitterCallback />
      },
      {
        path: "/netsuite/oauthcallback",
        element: <NetSuiteCallback />
      },
      {
        path: "/auto_responses",
        element: (
          <RouteHeader title="Auto Responses">
            <AutoResponses />
          </RouteHeader>
        )
      },
      {
        path: "/canned_responses",
        element: <CannedResponses />
      },
      {
        path: "/web_messenger",
        element: (
          <RouteHeader title="Web Messenger">
            <WebMessenger />
          </RouteHeader>
        )
      },
      {
        path: "/chat_window",
        element: (
          <RouteHeader
            title="Chat Window"
            headerActions={<CreateChatWindowButton />}
          >
            <ChatWindow />
          </RouteHeader>
        )
      },
      {
        path: "/chat_window/:chatWindowId",
        element: <ChatWindowEditor />
      },
      {
        path: "/work_schedules",
        element: (
          <RouteHeader title="Work Schedules">
            <WorkSchedules />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/netsuite",
        element: (
          <RouteHeader
            title="Netsuite CRM"
            headerActions={<BackToIntegrationLanding />}
          >
            <GatedSubscriptionFeature flagKey="enableNetsuite" name="NetSuite CRM">
              <NetSuite />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/netsuite/netsuitecreate",
        element: (
          <RouteHeader title="">
            <NetSuiteCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/formstack",
        element: (
          <RouteHeader title="Formstack" headerActions={<BackToIntegrations />}>
            <GatedSubscriptionFeature flagKey="enableFormstack" name="Formstack">
              <Formstack />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/formstack/formstackcreate",
        element: (
          <RouteHeader title="">
            <FormstackCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic",
        element: (
          <RouteHeader title="Accela" headerActions={<BackToIntegrations />}>
            <GatedSubscriptionFeature flagKey="enableAccela" name="Accela Civic">
              <AccelaCivic />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic/AccelaCreate",
        element: (
          <RouteHeader title="Accela">
            <AccelaCivicCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic/AccelaCallbackBridge",
        element: (
          <RouteHeader title="Accela">
            <AccelaCallbackBridge />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/Shopify",
        element: (
          <RouteHeader title="Shopify" headerActions={<BackToIntegrations />}>
            <GatedAdminFeature flagKey="enableShopify" name="Shopify">
              <Shopify />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/shopify/ShopifyCreate",
        element: (
          <RouteHeader
            title=""
            headerActions={<ShopifyBackToShopifyOverview />}
          >
            <ShopifyCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/shopify/ShopifyOverview",
        element: (
          <RouteHeader
            title=""
            headerActions={<ShopifyBackToIntegrationLanding />}
          >
            <ShopifyOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/log",
        element: (
          <RouteHeader title="Integration Log" headerActions={<BackToIntegrations />}>
            <IntegrationLog />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/mcp",
        element: (
          <RouteHeader title="MCP Servers" headerActions={<BackToIntegrations />}>
            <McpServers />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/payments/stripe",
        element: (
          <RouteHeader title="Stripe Payments" headerActions={<BackToIntegrations />}>
            <StripePayments />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/hubspot",
        element: (
          <RouteHeader title="HubSpot CRM" headerActions={<BackToIntegrationLandingHubSpot />}>
            <GatedAdminFeature flagKey="enableHubSpot" name="HubSpot CRM">
              <HubSpot />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/hubspot/overview",
        element: (
          <RouteHeader title="" headerActions={<HubSpotBackToIntegrationLanding />}>
            <HubSpotOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/hubspot/create",
        element: (
          <RouteHeader title="" headerActions={<HubSpotBackToHubSpotOverview />}>
            <HubSpotCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/bigcommerce",
        element: (
          <RouteHeader title="BigCommerce" headerActions={<BackToIntegrationLandingBigCommerce />}>
            <GatedAdminFeature flagKey="enableBigCommerce" name="BigCommerce">
              <BigCommerce />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/bigcommerce/overview",
        element: (
          <RouteHeader title="" headerActions={<BigCommerceBackToIntegrationLanding />}>
            <BigCommerceOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/bigcommerce/create",
        element: (
          <RouteHeader title="" headerActions={<BigCommerceBackToOverview />}>
            <BigCommerceCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/magento",
        element: (
          <RouteHeader title="Magento / Adobe Commerce" headerActions={<BackToIntegrationLandingMagento />}>
            <GatedAdminFeature flagKey="enableMagento" name="Magento / Adobe Commerce">
              <Magento />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/magento/overview",
        element: (
          <RouteHeader title="" headerActions={<MagentoBackToIntegrationLanding />}>
            <MagentoOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/magento/create",
        element: (
          <RouteHeader title="" headerActions={<MagentoBackToOverview />}>
            <MagentoCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/woocommerce",
        element: (
          <RouteHeader title="WooCommerce" headerActions={<BackToIntegrationLandingWooCommerce />}>
            <GatedAdminFeature flagKey="enableWooCommerce" name="WooCommerce">
              <WooCommerce />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/dynamics",
        element: (
          <RouteHeader title="Microsoft Dynamics 365" headerActions={<BackToIntegrationLandingDynamics />}>
            <GatedAdminFeature flagKey="enableDynamics" name="Microsoft Dynamics 365">
              <Dynamics />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/dynamics/overview",
        element: (
          <RouteHeader title="" headerActions={<DynamicsBackToIntegrationLanding />}>
            <DynamicsOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/dynamics/create",
        element: (
          <RouteHeader title="" headerActions={<DynamicsBackToOverview />}>
            <DynamicsCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/velaro-crm",
        element: (
          <RouteHeader title="Velaro CRM" headerActions={<BackToIntegrationLandingNativeCrm />}>
            <NativeCrm />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/salesforce",
        element: (
          <RouteHeader title="Salesforce CRM" headerActions={<BackToIntegrationLandingSalesforce />}>
            <GatedAdminFeature flagKey="enableSalesforce" name="Salesforce CRM">
              <Salesforce />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/servicenow",
        element: (
          <RouteHeader title="ServiceNow ITSM" headerActions={<BackToIntegrationLandingServiceNow />}>
            <ServiceNow />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/servicenow/create",
        element: (
          <RouteHeader title="Connect ServiceNow" headerActions={<BackToIntegrationLandingServiceNow />}>
            <ServiceNowCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/sap",
        element: (
          <RouteHeader title="SAP Business One" headerActions={<BackToIntegrations />}>
            <SAP />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/sap/create",
        element: (
          <RouteHeader title="Connect SAP" headerActions={<BackToIntegrations />}>
            <SapCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/uplisting",
        element: (
          <RouteHeader title="Uplisting" headerActions={<BackToIntegrationLandingUplisting />}>
            <GatedAdminFeature flagKey="enableUplisting" name="Uplisting">
              <Uplisting />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/uplisting/create",
        element: (
          <RouteHeader title="Connect Uplisting" headerActions={<BackToIntegrationLandingUplisting />}>
            <UplistingCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/guesty",
        element: (
          <RouteHeader title="Guesty" headerActions={<BackToIntegrationLandingGuesty />}>
            <GatedAdminFeature flagKey="enableGuesty" name="Guesty">
              <Guesty />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/guesty/create",
        element: (
          <RouteHeader title="Connect Guesty" headerActions={<BackToIntegrationLandingGuesty />}>
            <GuestyCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/cloudbeds",
        element: (
          <RouteHeader title="Cloudbeds" headerActions={<BackToIntegrationLandingCloudbeds />}>
            <GatedAdminFeature flagKey="enableCloudbeds" name="Cloudbeds">
              <Cloudbeds />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/cloudbeds/create",
        element: (
          <RouteHeader title="Connect Cloudbeds" headerActions={<BackToIntegrationLandingCloudbeds />}>
            <CloudbedsCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/pipedrive",
        element: (
          <RouteHeader title="Pipedrive CRM" headerActions={<BackToIntegrationLandingPipedrive />}>
            <GatedAdminFeature flagKey="enablePipedrive" name="Pipedrive">
              <Pipedrive />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/quickbooks",
        element: (
          <RouteHeader title="QuickBooks Online" headerActions={<BackToIntegrationLandingQuickBooks />}>
            <GatedAdminFeature flagKey="enableQuickBooks" name="QuickBooks Online">
              <QuickBooks />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/zoho",
        element: (
          <RouteHeader title="Zoho CRM" headerActions={<BackToIntegrationLandingZoho />}>
            <GatedAdminFeature flagKey="enableZoho" name="Zoho CRM">
              <Zoho />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/toast",
        element: (
          <RouteHeader title="Toast POS" headerActions={<BackToIntegrationLandingToast />}>
            <GatedAdminFeature flagKey="enableToast" name="Toast POS">
              <Toast />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/clover",
        element: (
          <RouteHeader title="Clover POS" headerActions={<BackToIntegrationLandingClover />}>
            <GatedSubscriptionFeature flagKey="enableCloverSkills" name="Clover POS">
              <Clover />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/mindbody",
        element: (
          <RouteHeader title="Mindbody" headerActions={<BackToIntegrationLandingMindbody />}>
            <GatedAdminFeature flagKey="enableMindbody" name="Mindbody">
              <Mindbody />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/mindbody/create",
        element: (
          <RouteHeader title="Connect Mindbody" headerActions={<BackToIntegrationLandingMindbody />}>
            <GatedAdminFeature flagKey="enableMindbody" name="Mindbody">
              <MindbodyCreate />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/monday",
        element: (
          <RouteHeader title="Monday.com" headerActions={<BackToIntegrationLandingMonday />}>
            <GatedAdminFeature flagKey="enableMonday" name="Monday.com">
              <Monday />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/recruiting",
        element: (
          <RouteHeader title="Recruiting Bot" headerActions={<BackToIntegrationLandingRecruiting />}>
            <GatedSubscriptionFeature flagKey="enableRecruitingBot" name="Recruiting Bot">
              <Recruiting />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/jobber",
        element: (
          <RouteHeader title="Jobber" headerActions={<BackToIntegrationLandingJobber />}>
            <GatedAdminFeature flagKey="enableJobber" name="Jobber">
              <Jobber />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/servicetitan",
        element: (
          <RouteHeader title="ServiceTitan" headerActions={<BackToIntegrations />}>
            <GatedAdminFeature flagKey="enableServiceTitan" name="ServiceTitan">
              <ServiceTitanPage />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/airtable",
        element: (
          <RouteHeader title="Airtable" headerActions={<BackToIntegrations />}>
            <GatedAdminFeature flagKey="enableAirtable" name="Airtable">
              <AirtablePage />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/power-automate",
        element: (
          <RouteHeader title="Power Automate" headerActions={<BackToIntegrations />}>
            <PowerAutomate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/make",
        element: (
          <RouteHeader title="Make (Integromat)" headerActions={<BackToIntegrations />}>
            <MakeIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/zapier",
        element: (
          <RouteHeader title="Zapier" headerActions={<BackToIntegrations />}>
            <ZapierIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/transcript-storage",
        element: (
          <RouteHeader title="Transcript Storage" headerActions={<BackToIntegrations />}>
            <TranscriptStorage />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/microsoft-365",
        element: (
          <RouteHeader title="Microsoft 365" headerActions={<BackToIntegrations />}>
            <Microsoft365 />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/microsoft-bookings",
        element: (
          <RouteHeader title="Microsoft Bookings" headerActions={<BackToIntegrationLandingMicrosoftBookings />}>
            <GatedSubscriptionFeature flagKey="enableMicrosoftBookingsSkills" name="Microsoft Bookings">
              <MicrosoftBookings />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/opentable",
        element: (
          <RouteHeader title="OpenTable" headerActions={<BackToIntegrationLandingOpenTable />}>
            <OpenTableIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/resy",
        element: (
          <RouteHeader title="Resy" headerActions={<BackToIntegrationLandingResy />}>
            <ResyIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/microsoft-365/teams-notifications",
        element: (
          <RouteHeader title="Teams Notifications" headerActions={<BackToIntegrations backTo="/integrations/microsoft-365" />}>
            <TeamsNotifications />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/microsoft-365/onedrive",
        element: (
          <RouteHeader title="OneDrive File Attachments" headerActions={<BackToIntegrations backTo="/integrations/microsoft-365" />}>
            <OneDriveSettingsPage />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/teams-notifications",
        element: (
          <RouteHeader title="Teams Notifications" headerActions={<BackToIntegrations />}>
            <TeamsNotifications />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/teams-channel",
        element: (
          <RouteHeader title="Teams Channel" headerActions={<BackToIntegrations />}>
            <TeamsChannel />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/slack-notifications",
        element: (
          <RouteHeader title="Slack Notifications" headerActions={<BackToIntegrations />}>
            <SlackNotificationsPage />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/alert-rules",
        element: (
          <RouteHeader title="Alert Rules" headerActions={<BackToIntegrations />}>
            <AlertRulesPage />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/gmail-inbox",
        element: (
          <RouteHeader title="Gmail Inbox" headerActions={<BackToIntegrations />}>
            <GmailInbox />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/outlook-inbox",
        element: (
          <RouteHeader title="Outlook Inbox" headerActions={<BackToIntegrations />}>
            <OutlookInbox />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/outlook-calendar",
        element: (
          <RouteHeader title="Outlook Calendar Booking" headerActions={<BackToIntegrations />}>
            <OutlookCalendar />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/calendly",
        element: (
          <RouteHeader title="Calendly" headerActions={<BackToIntegrations />}>
            <CalendlyIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/square-appointments",
        element: (
          <RouteHeader title="Square Appointments" headerActions={<BackToIntegrations />}>
            <SquareIntegration />
          </RouteHeader>
        )
      },
      {
        // OAuth2 callback landing page — Square redirects here with ?code=...&state=...
        path: "/integrations/square/callback",
        element: <SquareCallbackPage />
      },
      {
        path: "/integrations/klaviyo",
        element: (
          <RouteHeader title="Klaviyo" headerActions={<BackToIntegrationLandingKlaviyo />}>
            <KlaviyoIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/vast",
        element: (
          <RouteHeader title="VAST Retail" headerActions={<BackToIntegrationLandingVast />}>
            <GatedAdminFeature flagKey="enableVast" name="VAST Retail">
              <Vast />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/nextiva",
        element: (
          <RouteHeader title="Nextiva" headerActions={<BackToIntegrationLandingNextiva />}>
            <GatedAdminFeature flagKey="enableNextiva" name="Nextiva">
              <NextivaPage />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/teams-phone",
        element: (
          <RouteHeader title="Microsoft Teams Phone" headerActions={<BackToIntegrationLandingTeamsPhone />}>
            <GatedAdminFeature flagKey="enableNextiva" name="Microsoft Teams Phone">
              <TeamsPhonePage />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/skyswitch",
        element: (
          <RouteHeader title="SkySwitch" headerActions={<BackToIntegrationLandingSkySwitch />}>
            <GatedAdminFeature flagKey="enableNextiva" name="SkySwitch">
              <SkySwitchPage />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/secure-forms",
        element: (
          <RouteHeader title="SecureForms" headerActions={<BackToIntegrationLandingSecureForms />}>
            <GatedAdminFeature flagKey="enableSecureForms" name="SecureForms">
              <SecureFormsPanel />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/webflow",
        element: (
          <RouteHeader title="Webflow" headerActions={<BackToIntegrations />}>
            <GatedAdminFeature flagKey="enableWebflow" name="Webflow">
              <Webflow />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/webflow/connect",
        element: (
          <RouteHeader title="Connect Webflow Site" headerActions={<BackToIntegrations />}>
            <WebflowConnect />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/gorgias",
        element: (
          <RouteHeader title="Migrate from Gorgias" headerActions={<BackToIntegrationLandingGorgias />}>
            <GorgiasWizard />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/google-tag-manager",
        element: (
          <RouteHeader title="Google Tag Manager" headerActions={<BackToIntegrationLandingGTM />}>
            <GoogleTagManager />
          </RouteHeader>
        )
      },
      {
        // "Build Your Bot" 5-step wizard — new entry point, does not touch existing /bots routes
        path: "/chatbots/new",
        element: <BuildBotWizard />
      },
      {
        // Language & Translation setup wizard — additive, does not override existing config
        path: "/language-setup",
        element: <LanguageSetupWizard />
      },
      {
        path: "/bots",
        element: (
          <RouteHeader title="Bot Personas">
            <BotManager />
          </RouteHeader>
        )
      },
      {
        path: "/bots/training/index",
        element: (
          <StickyRouteHeader
            title="AI Training"
            headerActions={<CreateIndexButton></CreateIndexButton>}
          >
            <AIChatbotTrainingIndex />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/web",
        element: (
          <StickyRouteHeader title="AI Training" headerActions={null}>
            <AIChatbotWebTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/file",
        element: (
          <StickyRouteHeader title="AI Training" headerActions={null}>
            <AIChatbotFileTraining />
          </StickyRouteHeader>
        )
      },
      // /ai/* → redirect to /ai-new/* (all old chatbot URLs forward to the new UI)
      { path: "/ai", element: <Navigate to="/ai-new/settings" replace /> },
      { path: "/ai/settings/general/:id", element: <RedirectAIToNew suffix="general" /> },
      { path: "/ai/settings/training/:id", element: <RedirectAIToNew suffix="general" /> },
      { path: "/ai/settings/handoff/:id", element: <RedirectAIToNew suffix="handoff" /> },
      { path: "/ai/settings/playground/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      { path: "/ai/settings/knowledge-overrides/:id", element: <RedirectAIToNew suffix="knowledge-overrides" /> },
      { path: "/ai/settings/suppressed-content/:id", element: <RedirectAIToNew suffix="suppressed-content" /> },
      { path: "/ai/settings/qna/:id", element: <RedirectAIToNew suffix="qna" /> },
      { path: "/ai/settings/skills/:id", element: <RedirectAIToNew suffix="skills" /> },
      { path: "/ai/settings/test/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      // {
      //   path: "/ai/settings/data/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsData />
      //     </StickyRouteHeader>
      //   )
      // },
      // {
      //   path: "/ai/settings/questions/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsStarterQuestions />
      //     </StickyRouteHeader>
      //   )
      // },
      {
        path: "/ai-new/settings",
        element: (
          <StickyRouteHeader
            title="AI Chatbots"
            headerActions={<AINewCreateChatbotButton />}
          >
            <AINewSettings />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/general/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsGeneral />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/handoff/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsHandoff />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/test-chat/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsPlayground />
          </StickyRouteHeader>
        )
      },
      // Legacy playground path → redirect to test-chat
      { path: "/ai-new/settings/playground/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      {
        path: "/ai-new/settings/knowledge-overrides/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsKnowledgeOverrides />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/suppressed-content/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            backLink="/ai-new/settings"
          >
            <AIChatbotSettingsSuppressedContent />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/skills/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            backLink="/ai-new/settings"
          >
            <AINewSettingsSkills />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/channel-overrides/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsChannelOverrides />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/qna/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsQnA />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/training",
        element: (
          <StickyRouteHeader title="Knowledge & Training">
            <AINewTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/chat",
        element: <Navigate to="/ai-new/settings" replace />
      },
      {
        path: "/ai-new/indexes",
        element: (
          <StickyRouteHeader title="Knowledge Indexes">
            <AINewIndexes />
          </StickyRouteHeader>
        )
      },
      {
        path: "/rules",
        element: (
          <RouteHeader title="Routing Rules">
            <RuleManager />
          </RouteHeader>
        )
      },
      {
        path: "/routing-history",
        element: (
          <RouteHeader title="Routing History">
            <RoutingHistory />
          </RouteHeader>
        )
      },
      {
        path: "/whats-running",
        element: (
          <RouteHeader title="What's Running">
            <WhatsRunning />
          </RouteHeader>
        )
      },
      {
        path: "/chat-preview",
        element: (
          <RouteHeader title="Preview Chat">
            <ChatPreview />
          </RouteHeader>
        )
      },
      {
        path: "/files",
        element: (
          <RouteHeader title="File Upload">
            <FileStorage />
          </RouteHeader>
        )
      },
      {
        path: "/workflows/create",
        element: <WorkflowsCreate />
      },
      {
        path: "/workflows",
        element: <WorkflowsLandingGate />
      },
      {
        path: "/workflows/variable-guide",
        element: (
          <RouteHeader title="Variable Guide">
            <VariableGuide />
          </RouteHeader>
        )
      },
      {
        path: "/workflows/:id",
        element: <WorkflowsEditor />
      },
      {
        path: "/invitations",
        element: (
          <RouteHeader title="Invitations">
            <Invitations />
          </RouteHeader>
        )
      },
      {
        path: "/skills",
        element: (
          <RouteHeader title="Skills">
            <Skills />
          </RouteHeader>
        )
      },

      {
        path: "/kb/articles",
        element: (
          <RouteHeader title="KB Articles">
            <ArticlesIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/settings",
        element: (
          <RouteHeader title="KB Settings">
            <SettingsIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/articles/create",
        element: (
          <RouteHeader title="Create KB Article">
            <ArticlesCreate />
          </RouteHeader>
        )
      },
      //todo this id needs fixed
      {
        path: "/kb/articles/edit/:id",
        element: (
          <RouteHeader title="Edit KB Article">
            <ArticlesEdit />
          </RouteHeader>
        )
      },
      {
        path: "/kb/topics",
        element: (
          <RouteHeader title="KB Topics">
            <TopicsIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/map",
        element: (
          <RouteHeader title="KB Site Map">
            <SiteMap />
          </RouteHeader>
        )
      },
      {
        path: "/kb/setup",
        element: (
          <RouteHeader title="KB Setup">
            <SetupIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/performance",
        element: (
          <RouteHeader title="KB Performance">
            <KBPerformance />
          </RouteHeader>
        )
      },
      {
        path: "/kb/widget",
        element: (
          <RouteHeader title="KB Search Widget">
            <KBWidgetEmbedCode />
          </RouteHeader>
        )
      },
      {
        path: "/kb/assets",
        element: (
          <RouteHeader title="KB Asset Library">
            <AssetsIndex />
          </RouteHeader>
        )
      },
      {
        path: "/messaging/setup",
        element: <MessagingSetup />
      },
      {
        path: "/settings",
        element: (
          <RouteHeader title="Settings">
            <MessagingSettings />
          </RouteHeader>
        )
      },
      // Friendly deep-link aliases used by the copilot — redirect to /settings?tab=...
      {
        path: "/settings/developer",
        element: <Navigate to="/settings?tab=apiKeys" replace />
      },
      {
        path: "/settings/security",
        element: <Navigate to="/settings?tab=security" replace />
      },
      {
        path: "/billing",
        element: <BillingPage />
      },
      {
        path: "/billing/contracts",
        element: (
          <RouteHeader title="Contracts">
            <Contracts />
          </RouteHeader>
        )
      },
      {
        path: "/billing/contracts/templates",
        element: (
          <RouteHeader title="Contract Templates">
            <ContractTemplates />
          </RouteHeader>
        )
      },
      {
        path: "/billing/invoices",
        element: (
          <RouteHeader title="Invoices">
            <BillingInvoices />
          </RouteHeader>
        )
      },
      {
        path: "/billing/templates",
        element: (
          <RouteHeader title="Invoice Templates">
            <BillingTemplates />
          </RouteHeader>
        )
      },
      {
        path: "/billing/settings",
        element: (
          <RouteHeader title="Stripe Settings">
            <BillingStripeSettings />
          </RouteHeader>
        )
      },
      {
        path: "/page-widgets",
        element: (
          <RouteHeader title="Page Widgets">
            <PageWidgets />
          </RouteHeader>
        )
      },
      {
        path: "/proactive-engagement",
        element: (
          <RouteHeader title="Proactive Engagement">
            <ProactiveEngagement />
          </RouteHeader>
        )
      },
      {
        path: "/visitor-monitor",
        element: (
          <RouteHeader title="Visitor Monitor">
            <VisitorMonitor />
          </RouteHeader>
        )
      },
      {
        path: "/email-campaigns",
        element: (
          <RouteHeader title="Email Campaigns">
            <EmailCampaigns />
          </RouteHeader>
        )
      },
      {
        path: "/email-flows",
        element: (
          <RouteHeader title="Email Automation Flows">
            <EmailFlows />
          </RouteHeader>
        )
      },
      {
        path: "/ivr-campaigns",
        element: (
          <RouteHeader title="IVR Outbound Campaigns">
            <IvrCampaigns />
          </RouteHeader>
        )
      },
      {
        path: "/admin/packages",
        element: (
          <RouteHeader title="Solution Packages">
            <Packages />
          </RouteHeader>
        )
      },
      {
        path: "/support",
        element: (
          <RouteHeader title="Contact Support">
            <ContactSupport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/value",
        element: (
          <RouteHeader title="Value Report">
            <ValueReport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/agent-performance",
        element: (
          <RouteHeader title="Agent Performance">
            <AgentPerformance />
          </RouteHeader>
        )
      },
      {
        path: "/reports/leaderboard",
        element: (
          <RouteHeader title="Agent Leaderboard">
            <Leaderboard />
          </RouteHeader>
        )
      },
      {
        path: "/reports/chat-intelligence",
        element: (
          <RouteHeader title="Chat Intelligence">
            <ChatIntelligence />
          </RouteHeader>
        )
      },
      {
        path: "/reports/csat",
        element: (
          <RouteHeader title="CSAT Survey Results">
            <CsatReport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/conversions",
        element: (
          <RouteHeader title="Conversion Attribution">
            <ConversionReport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/apple-messages",
        element: (
          <RouteHeader title="Apple Messages Analytics">
            <AppleMessagesReport />
          </RouteHeader>
        )
      },
      {
        path: "/settings/conversions",
        element: (
          <RouteHeader title="Conversion Goals">
            <Conversions />
          </RouteHeader>
        )
      },
      { path: "*", element: <NotFoundPage /> }
    ]
  }
];

const mainLoader = async ({ request }: any) => {
  const url = new URL(request.url);
  const route = url.pathname;
  if (AuthService.isAuthenticated() && route === "/") {
    return redirect("/");
  }
  return null;
};

const appRouter = createBrowserRouter(createAppRoutes(mainLoader));

export function AppRoutes() {
  return <RouterProvider router={appRouter} />;
}
